import '@connected/ui/globals.css';

import '@/assets/styles/index.css';
import '@/lib/lang';

import type { ReactNode } from 'react';

import { LoggerProvider } from '@connected/api-client/components/providers/logger';
import { useAuthentication } from '@connected/api-client/hooks/use-authentication';
import { ScreenSizeProvider } from '@connected/ui/providers/screen-size';
import { ThemeProvider } from '@connected/ui/providers/theme';
import { useIsRestoring } from '@tanstack/react-query';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import { RouterProvider } from '@tanstack/react-router';
import { StrictMode, Suspense, useEffect, useRef } from 'react';
import { createRoot } from 'react-dom/client';

import { Loading } from '@/components/loading';
import { env } from '@/config/env';
import { useOnline } from '@/hooks/use-online';
import { logger } from '@/lib/logger';
import { localStoragePersister } from '@/lib/persister';
import { queryClient } from '@/lib/query-client';
import { router } from '@/lib/router';

import { LegacyProvider } from './components/providers/legacy';

export function App() {
    const auth = useAuthentication();

    return <RouterProvider router={router} context={{ queryClient, ...auth }} />;
}

// TODO: Implement the before login redirect modal
function PersistQueryClientHydrationHandler() {
    // const [open, setOpen] = useState(false);
    const isOnline = useOnline();
    // Don't use redirectToLogin here - it runs before routes like /sso can process tokens
    // Auth redirects are handled by the (auth) route group's beforeLoad
    const auth = useAuthentication();
    const isRestoring = useIsRestoring();
    const hasHydrated = useRef(false);

    useEffect(() => {
        if (isRestoring) {
            return;
        }

        async function resumeQuerieCleint() {
            if (isOnline && auth.isAuthenticated) {
                await queryClient.resumePausedMutations();

                if (!hasHydrated.current) {
                    hasHydrated.current = true;
                    queryClient.invalidateQueries();
                }

                logger.info({
                    system: 'PersitQueryClient',
                    message: 'Successfully hydrated query client',
                });
            }
            else {
                logger.info({
                    system: 'PersitQueryClient',
                    message: 'Not online or not authenticated, skipping resumePausedMutations',
                });
            }
        }

        resumeQuerieCleint();
    }, [isRestoring, isOnline, auth.isAuthenticated]);

    return null;

    // return (
    //     <Popout open={open}>
    //         <Popout.Content>
    //             <Popout.Header>
    //             </Popout.Header>
    //             <Popout.Footer>
    //             </Popout.Footer>
    //         </Popout.Content>
    //     </Popout>
    // );
}

function PersitQueryClient({ children }: { children: ReactNode }) {
    return (
        <PersistQueryClientProvider
            client={queryClient}
            persistOptions={{
                persister: localStoragePersister,
                buster: env.appVersion,
            }}
        >
            <PersistQueryClientHydrationHandler />
            {children}
        </PersistQueryClientProvider>
    );
}

const rootElement = document.getElementById('root');
rootElement?.setAttribute('notranslate', 'true');

if (rootElement && !rootElement.innerHTML) {
    createRoot(rootElement).render(
        <StrictMode>
            <LegacyProvider>
                <LoggerProvider logger={logger}>
                    <PersitQueryClient>
                        <ThemeProvider>
                            <ScreenSizeProvider>
                                <Suspense fallback={<Loading action="Authenticating" />}>
                                    <App />
                                </Suspense>
                            </ScreenSizeProvider>
                        </ThemeProvider>
                    </PersitQueryClient>
                </LoggerProvider>
            </LegacyProvider>
        </StrictMode>,
    );
}
